import { Avatar, Button, Card, Tooltip, cn } from "@heroui/react";
import { Icon } from "@iconify/react";
import {
	CalendarDate,
	type DateFields,
	getLocalTimeZone,
	today,
} from "@internationalized/date";

import { OpenMode, useLocalStorageForm } from "@onesolution/components";
import { OPERATION_STATUS } from "@onesolution/constants";
import { type AppFormApi, useLanguage } from "@onesolution/hooks";

import type { MONDAY_TYPES } from "@onesolution/monday";
import {
	createOperationOnServer,
	updateOperationOnServer,
} from "@onesolution/serverFn";
import { useIsFetching } from "@tanstack/react-query";
import CampaignsSelector from "../form/Selectors/CampaignsSelector";
import EntitiesSelector from "../form/Selectors/EntitiesSelector";
import MarketingPlansSelector from "../form/Selectors/MarketingPlansSelector";
import ServicesSelector from "../form/Selectors/ServicesSelector";
import { OperationProductForm } from "./OperationProductForm";

const bufferDate = new CalendarDate(2020, 1, 1);

export type Product = {
	id: string;
	name: string;
	prices?: number;
};
export type RequestedProductFieldApi = {
	product: Product;
	quantity: number;
	deliveryDate: CalendarDate | null;
	isNew?: boolean;
	ids?: string[];
};

export type OperationFormData = {
	id?: string;
	name: string;
	deadline: CalendarDate | null;
	timeline: {
		start: CalendarDate | null;
		end: CalendarDate | null;
	};
	thumbnail: File[] | MONDAY_TYPES["file"];
	services: string[] | null;
	campaigns: string | null;
	description: string | null;
	marketingPlans: string | null;
	entities: string[] | null;
	deleteThumbnail: boolean;
	files: File[] | MONDAY_TYPES["file"];
	deletedProducts?: Array<string>;
	boxFolderId: string | null;
	requestedProducts: Array<RequestedProductFieldApi>;
	existingProducts?: Array<RequestedProductFieldApi>;
};

export const defaultOperationFormValues = {
	name: "",
	deadline: null,
	timeline: {
		start: null,
		end: null,
	},
	services: [],
	campaigns: null,
	description: null,
	marketingPlans: null,
	entities: null,
	files: [],
	thumbnail: [],
	boxFolderId: null,
	deleteThumbnail: false,
	deletedProducts: [],
	requestedProducts: [
		{
			product: { id: "", name: "", prices: 0 },
			deliveryDate: null,
			quantity: 1,
			isNew: false,
			ids: [],
		},
	],
} as OperationFormData;

export default function OperationForm({
	onClose,
	mode,
	loadedData,
}: {
	onClose: () => void;
	mode: OpenMode;
	loadedData?: OperationFormData;
}) {
	const { t, locale } = useLanguage();
	const isDataLoading = useIsFetching() !== 0;

	const createOperation = createOperationOnServer.useMutation();
	const updateOperation = updateOperationOnServer.useMutation();
	const mutationMapping = {
		[OpenMode.creation]: createOperation,
		[OpenMode.edition]: updateOperation,
	};
	const isMutationLoading =
		createOperation.isPending || updateOperation.isPending;

	const form = useLocalStorageForm<OperationFormData>({
		mode,
		localStorageKey: "@onevision-operations",
		loadedData,
		defaultValues: defaultOperationFormValues,
		formatOnLoad: (values) => {
			const saved = values as {
				deadline?: string;
				timeline?: { start: string; end: string; files: Array<unknown> };
				files?: { relativePath: string }[];
				thumbnail?: { relativePath: string }[];
				requestedProducts: Array<RequestedProductFieldApi>;
			};

			if (saved.deadline) {
				Object.assign(values, {
					deadline: bufferDate.set(saved.deadline as DateFields).copy(),
				});
			}
			for (const product of saved.requestedProducts) {
				if (product.deliveryDate) {
					Object.assign(product, {
						deliveryDate: bufferDate
							.set(product.deliveryDate as DateFields)
							.copy(),
					});
				}
			}

			if (saved.timeline?.start && saved.timeline?.end) {
				Object.assign(values, {
					timeline: {
						start: bufferDate.set(saved.timeline.start as DateFields).copy(),
						end: bufferDate.set(saved.timeline.end as DateFields).copy(),
					},
				});
			}

			Object.assign(values, {
				files: saved.files?.filter((f) => !f.relativePath) || [],
				thumbnail: saved.thumbnail?.filter((f) => !f.relativePath) || [],
			});

			return values;
		},
		onSuccess: onClose,
		onSubmitMutation: async ({ value, meta: status }, mutationOpts) => {
			if (mode === OpenMode.close || mode === OpenMode.details) return;

			return mutationMapping[mode].mutate(
				{
					id: loadedData?.id,
					name: value.name,
					deadline: value.deadline?.toString(),
					timeline:
						value.timeline?.start && value.timeline?.end
							? {
									from: value.timeline.start?.toString(),
									to: value.timeline.end?.toString(),
								}
							: null,
					description: value.description,
					status: status as string,
					services: value.services,
					campaigns: value.campaigns,
					thumbnail:
						value.thumbnail?.[0] instanceof File
							? value.thumbnail?.[0]
							: undefined,
					deleteThumbnail: value.deleteThumbnail,
					marketingPlans: value.marketingPlans,
					entities: value.entities,
					files: value.files.filter((f) => f instanceof File),
					deletedProducts: value.deletedProducts,
					subItems: (mode === OpenMode.edition
						? value.requestedProducts.filter((p) => p.isNew)
						: value.requestedProducts
					)
						.filter((requested) => requested.product?.id)
						.map((prod) => ({
							id: prod.product!.id,
							quantity: prod.quantity,
							deliveryDate: prod.deliveryDate?.toString(),
							isNew: prod.isNew,
						})),
				},
				mutationOpts(),
			);
		},
	});

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				const status = (
					(e?.nativeEvent as SubmitEvent).submitter as HTMLButtonElement
				).value;

				form.handleSubmit(status);
			}}
			className="flex gap-4 flex-col max-w-min"
		>
			<form.AppField name="name">
				{(field) => (
					<field.Input
						isRequired
						label={t("basic.name")}
						placeholder={t("operation.name-info")}
					/>
				)}
			</form.AppField>
			<div className="flex gap-4">
				<form.AppField name="deadline">
					{(field) => (
						<field.DatePicker
							label={t("basic.delivery_date")}
							minValue={today(getLocalTimeZone())}
						/>
					)}
				</form.AppField>
				<form.AppField name="timeline">
					{(field) => (
						<field.DateRangePicker
							label={t("basic.timeline")}
							aria-label="Timeline"
							minValue={today(getLocalTimeZone())}
						/>
					)}
				</form.AppField>
			</div>
			<div className="flex gap-4">
				<MarketingPlansSelector
					name="marketingPlans"
					isRequired
					form={form as AppFormApi}
				/>
				<CampaignsSelector
					name="campaigns"
					isRequired
					form={form as AppFormApi}
				/>
			</div>
			<div className="flex gap-4">
				<EntitiesSelector
					name="entities"
					isRequired
					form={form as AppFormApi}
				/>
				<ServicesSelector
					name="services"
					isRequired
					form={form as AppFormApi}
				/>
			</div>
			<form.AppField name="description">
				{(field) => (
					<field.TextArea
						label={t("basic.description")}
						placeholder={t("operation.enter-description")}
						description={t("operation.description-info")}
					/>
				)}
			</form.AppField>
			<form.AppField name="files">
				{(field) => (
					<>
						<field.FilePicker
							cantDeleteExisting
							multiple
							label={t("file.attachments")}
						/>
						<p className="text-gray-400 text-xs">{t("message.errorFile")}</p>
					</>
				)}
			</form.AppField>
			<form.AppField name="thumbnail">
				{(field) => {
					const thumbnail =
						field.state.value?.[0] &&
						("url" in field.state.value[0]
							? (field.state.value[0]?.url as string)
							: URL.createObjectURL(field.state.value[0] as File));

					return (
						<div className="flex gap-4 items-center">
							{thumbnail && (
								<Avatar
									isBordered
									size="lg"
									radius="sm"
									className="w-40 h-full"
									src={thumbnail}
									showFallback={false}
								/>
							)}
							<field.FilePicker
								maxFiles={1}
								multiple={false}
								labelKey="file.attach-image"
								validator={(file) => {
									if (!file.type.includes("image"))
										return { code: "not-an-image", message: "not-an-image" };

									return null;
								}}
								label={t("name.thumbnail")}
								onFileRemoved={() =>
									form.setFieldValue("deleteThumbnail", true)
								}
							/>
						</div>
					);
				}}
			</form.AppField>

			<div className="flex flex-col gap-2">
				<span className="text-sm">
					{t("name.product")} & {t("name.services")}{" "}
				</span>
				<span className="text-tiny text-foreground-400">
					{t("operation.add-products-services")}
				</span>
				{mode === OpenMode.edition && (
					<form.AppField name="deletedProducts" mode="array">
						{(deletedProductsField) => (
							<div className="flex w-full gap-2 mb-4 flex-col">
								{loadedData?.existingProducts
									?.filter((prod) => !prod.isNew)
									?.map((prod) =>
										prod?.ids?.map((id) => {
											const deleted =
												form.state.values.deletedProducts?.includes(id);

											return (
												<Card
													key={id}
													className={cn(
														"w-full p-2 pl-6 flex flex-row gap-4 items-center",
														deleted && "bg-red-100",
													)}
												>
													<h4 className="text-foreground-700">
														{prod.product.name}
													</h4>
													<h6 className="text-foreground-400 italic!">
														{prod.deliveryDate?.toString()}
													</h6>
													<Tooltip
														content={
															deleted
																? `${t("basic.undo")} ${t("basic.delete")}`
																: t("basic.delete")
														}
													>
														<Button
															isIconOnly
															variant="light"
															className="p-1 ml-auto"
															onPress={() => {
																if (deleted) {
																	deletedProductsField.removeValue(
																		deletedProductsField.state.value?.findIndex(
																			(i) => id === i,
																		)!,
																	);
																} else {
																	deletedProductsField.pushValue(id);
																}
															}}
														>
															<Icon
																icon={
																	deleted
																		? "solar:undo-left-round-linear"
																		: "solar:trash-bin-trash-linear"
																}
															/>
														</Button>
													</Tooltip>
												</Card>
											);
										}),
									)}
							</div>
						)}
					</form.AppField>
				)}
				<form.AppField name="requestedProducts" mode="array">
					{(requestedProductsField) => {
						const totalPrice = requestedProductsField.state.value.reduce(
							(out, { product, quantity }) =>
								out + (product?.prices || 0) * (quantity || 0),
							0,
						);
						return (
							<div className="flex flex-col gap-2">
								{requestedProductsField.state.value.map(
									(fieldData: RequestedProductFieldApi, i: number) => {
										return (
											<form.AppField
												key={`${fieldData.product?.id}_${i}`}
												name={`requestedProducts[${i}].product.id`}
											>
												{(subField) => (
													<OperationProductForm
														index={i}
														requestedProductsField={requestedProductsField}
														defaultValues={
															mode === OpenMode.edition
																? ""
																: subField.state.value
														}
													/>
												)}
											</form.AppField>
										);
									},
								)}
								<Button
									className="max-w-min"
									variant="bordered"
									startContent={
										<Icon
											className="text-foreground text-lg"
											icon="solar:round-arrow-down-linear"
										/>
									}
									onPress={() =>
										requestedProductsField.pushValue({
											product: {
												id: "",
												name: "",
												prices: 0,
											},
											quantity: 1,
											deliveryDate: null,
											isNew: mode === OpenMode.edition,
										})
									}
									type="button"
								>
									{t("basic.add")} {t("name.product")}
								</Button>
								{totalPrice ? (
									<div className="rounded-xl bg-primary-100 p-4 flex text-primary-700">
										<Icon
											icon="solar:info-circle-bold"
											className="text-2xl text-primary-500 mr-4"
										/>
										<div className="flex flex-col">
											<span className="font-bold">
												{t("operation.total-title")}
											</span>
											{t("operation.total")}{" "}
											{totalPrice.toLocaleString(locale, {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})}{" "}
											€
										</div>
									</div>
								) : null}
							</div>
						);
					}}
				</form.AppField>
			</div>
			<form.Subscribe
				selector={(state) => [state.canSubmit, state.isSubmitting]}
			>
				{([canSubmit]) => (
					<div className="flex gap-2">
						<Button
							type="submit"
							color="primary"
							value={OPERATION_STATUS.ToBeCategorized}
							isLoading={isMutationLoading || isDataLoading}
							isDisabled={!canSubmit || isMutationLoading || isDataLoading}
							className="max-w-min"
						>
							{t("operation.send")}
						</Button>
						<Button
							type="submit"
							value={OPERATION_STATUS.Draft}
							isLoading={isMutationLoading || isDataLoading}
							isDisabled={!canSubmit || isMutationLoading || isDataLoading}
							className="max-w-min"
						>
							{t("operation.draft")}
						</Button>
					</div>
				)}
			</form.Subscribe>
		</form>
	);
}
